import { useState } from 'react';
import RegisterLink from '@/components/ui/RegisterLink';

type Mode = 'register' | 'login';

const REGISTER_STEPS = [
  {
    title: 'Visit bybid9.com', time: '10 seconds', icon: 'ri-global-line',
    desc: 'Open your browser or the Bybid9 app and go to bybid9.com. The platform works on desktop, tablet, and mobile.',
    tips: ['Bookmark bybid9.com for quick access', 'Confirm you are on the official site before signing up'],
    note: 'Always use the correct URL: https://bybid9.tips/RFLANDINGPAGESEO'
  },
  {
    title: 'Open Register: REGISTER tab', time: '5 seconds', icon: 'ri-user-add-line',
    desc: 'Tap Register to open the account panel. At the top, select the REGISTER tab (not LOGIN) so the registration form is shown.',
    tips: ['The active tab is underlined in cyan', 'Switch to LOGIN only if you already have an account'],
    note: 'Creating an account is free; you are not charged at this step.'
  },
  {
    title: 'Enter full name', time: '20 seconds', icon: 'ri-user-3-line',
    desc: 'In FULL NAME, type your legal name exactly as it appears on your bank account. The form reminds you: it must match your bank account name for withdrawals and verification.',
    tips: ['Match spelling to your bank records', 'Avoid nicknames if they differ from banking details'],
    note: 'A mismatch between your registered name and bank name can delay payouts.'
  },
  {
    title: 'Enter mobile number', time: '15 seconds', icon: 'ri-smartphone-line',
    desc: 'In MOBILE NO, enter your mobile number in the format shown on the form (for example 61480050689). This number is used for SMS codes and account security.',
    tips: ['Use the number you can receive SMS on', 'Double-check digits before continuing'],
    note: 'Your number is used for verification and login; keep it private.'
  },
  {
    title: 'Set password (6 to 20 characters)', time: '20 seconds', icon: 'ri-lock-password-line',
    desc: 'In PASSWORD, choose a password between 6 and 20 characters, as indicated on the form.',
    tips: ['Use a password you do not reuse on other sites', 'Consider a password manager'],
    note: 'Never share your password with anyone, including support.'
  },
  {
    title: 'Referrer code', time: '10 seconds', icon: 'ri-links-line',
    desc: 'The REFERRER CODE field may be pre-filled (for example RFTPAHEYLINK). Leave it or replace it only if you are using a different valid referral code.',
    tips: ['Use your referrer’s code if they gave you one', 'If pre-filled, you can keep the default unless instructed otherwise'],
    note: 'Referral codes connect your account to promotions where applicable.'
  },
  {
    title: 'SMS code: GET CODE', time: '1 minute', icon: 'ri-mail-send-line',
    desc: 'Read the line: “Press GET CODE Button To Receive Verification Code Via SMS.” Tap GET CODE, wait for the SMS, then enter the code in the Verification Code field.',
    tips: ['Request a new code only if the first SMS does not arrive', 'Enter the code before it expires'],
    note: 'If SMS fails repeatedly, contact 24/7 support (e.g. Telegram or WhatsApp).'
  },
  {
    title: 'Tap REGISTER', time: '5 seconds', icon: 'ri-checkbox-circle-line',
    desc: 'Review your details, then press the large REGISTER button (cyan-to-blue gradient) to finish creating your Bybid9 account.',
    tips: ['After success, you can log in with the LOGIN tab using mobile/username and password', 'Check promotions for welcome bonuses and deposit offers'],
    note: 'Bybid9 is 18+ only. Gamble responsibly.'
  },
];

const LOGIN_STEPS = [
  {
    title: 'Visit bybid9.com', time: '5 seconds', icon: 'ri-global-line',
    desc: 'Open bybid9.com in your browser or app.',
    tips: ['Bookmark the official site'],
    note: 'Confirm the URL is https://bybid9.tips/RFLANDINGPAGESEO'
  },
  {
    title: 'Open Login: LOGIN tab', time: '5 seconds', icon: 'ri-login-box-line',
    desc: 'Tap Login and ensure the LOGIN tab is selected at the top (underlined in cyan), not REGISTER.',
    tips: ['Active tab shows bold text and the cyan underline'],
    note: 'Use REGISTER only when creating a new account.'
  },
  {
    title: 'Mobile No / Username', time: '15 seconds', icon: 'ri-smartphone-line',
    desc: 'In MOBILE NO / USERNAME, enter the mobile number or username linked to your Bybid9 account (placeholder example: 61480050689).',
    tips: ['Use the same credentials you registered with'],
    note: 'Forgot which number you used? Contact support before guessing passwords.'
  },
  {
    title: 'Password & visibility', time: '15 seconds', icon: 'ri-lock-line',
    desc: 'In PASSWORD, enter your password (6 to 20 characters). Use the eye icon on the right to show or hide the password while typing.',
    tips: ['Use Forgot Password? if you cannot log in', 'Never enter your password on unofficial sites'],
    note: 'Keep your password secret at all times.'
  },
  {
    title: 'Keep me logged in (optional)', time: '5 seconds', icon: 'ri-checkbox-line',
    desc: 'Optionally tick Keep Me Logged In on your trusted device. Use Forgot Password? on the right if you need to reset access.',
    tips: ['Only use “keep logged in” on private devices', 'Log out on shared computers'],
    note: 'Reset links are sent securely when you use Forgot Password.'
  },
  {
    title: 'Tap LOGIN', time: '5 seconds', icon: 'ri-door-open-line',
    desc: 'Press the large LOGIN button (cyan-to-blue gradient, white text) to access your Bybid9 account.',
    tips: ['After login, check Deposit and Promotions from the lobby'],
    note: 'If login fails, verify caps lock and retry before resetting your password.'
  },
];

export default function RegisterGuideSection() {
  const [mode, setMode] = useState<Mode>('register');
  const [step, setStep] = useState(0);

  const steps = mode === 'register' ? REGISTER_STEPS : LOGIN_STEPS;
  const current = steps[step];

  const handleMode = (m: Mode) => { setMode(m); setStep(0); };

  return (
    <section className="relative py-14 sm:py-16 md:py-20 px-4 sm:px-5 md:px-8 overflow-hidden" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #071520 60%, #0d1117 100%)' }}>
      <div className="section-divider absolute top-0 left-0 w-full"></div>
      <div className="ribbon ribbon-2"></div>
      <div className="ribbon ribbon-4"></div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(71,202,241,0.06) 0%, transparent 60%)' }}></div>

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="badge-blue mb-5"><i className="ri-guide-line"></i> Step-by-Step Guide</div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-3 px-1">How to Register &amp; Login at Bybid9</h2>
          <p className="text-[#56626B] text-base max-w-2xl mx-auto leading-relaxed">Getting started at Bybid9 takes less than 3 minutes. Follow our detailed guide below to create your account and start playing today.</p>
        </div>

        {/* Mode toggle */}
        <div className="flex justify-center mb-8 sm:mb-10 px-1">
          <div className="flex w-full max-w-md p-1 rounded-2xl" style={{ background: 'rgba(71,202,241,0.06)', border: '1px solid rgba(71,202,241,0.15)' }}>
            <button
              type="button"
              onClick={() => handleMode('register')}
              className="flex-1 min-h-[48px] px-3 sm:px-8 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 cursor-pointer flex items-center justify-center gap-2"
              style={mode === 'register' ? { background: 'rgba(71,202,241,0.2)', color: '#47CAF1', border: '1px solid rgba(71,202,241,0.4)', boxShadow: '0 0 20px rgba(71,202,241,0.2)' } : { color: '#56626B' }}
            >
              <i className="ri-user-add-line shrink-0"></i>
              <span className="truncate">Register</span>
            </button>
            <button
              type="button"
              onClick={() => handleMode('login')}
              className="flex-1 min-h-[48px] px-3 sm:px-8 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 cursor-pointer flex items-center justify-center gap-2"
              style={mode === 'login' ? { background: 'rgba(71,202,241,0.2)', color: '#47CAF1', border: '1px solid rgba(71,202,241,0.4)', boxShadow: '0 0 20px rgba(71,202,241,0.2)' } : { color: '#56626B' }}
            >
              <i className="ri-login-box-line shrink-0"></i>
              <span className="truncate">Login</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Step list */}
          <div className="lg:col-span-1 space-y-2 max-h-[min(42vh,280px)] overflow-y-auto lg:max-h-none lg:overflow-visible pr-1 -mr-1 lg:pr-0 lg:mr-0">
            {steps.map((s, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className="w-full flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-xl text-left transition-all duration-200 cursor-pointer"
                style={step === i
                  ? { background: 'rgba(71,202,241,0.1)', border: '1px solid rgba(71,202,241,0.35)', boxShadow: '0 0 16px rgba(71,202,241,0.1)' }
                  : { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(71,202,241,0.06)' }
                }
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-black transition-all duration-200"
                  style={step === i
                    ? { background: '#47CAF1', color: '#0d1117', boxShadow: '0 0 12px rgba(71,202,241,0.5)' }
                    : { background: 'rgba(71,202,241,0.08)', color: '#56626B' }
                  }
                >
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate" style={{ color: step === i ? '#47CAF1' : '#56626B' }}>{s.title}</div>
                  <div className="text-[10px]" style={{ color: 'rgba(86,98,107,0.6)' }}>{s.time}</div>
                </div>
                {step === i && <i className="ri-arrow-right-s-line text-[#47CAF1] flex-shrink-0"></i>}
              </button>
            ))}
            <div className="pt-4">
              <RegisterLink section="register_guide" linkLabel="Register Now" href="https://bybid9.tips/RFLANDINGPAGESEO" className="btn-premium w-full text-center text-sm py-3 block"><span>Register Now</span></RegisterLink>
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            <div className="glass-card rounded-2xl p-5 sm:p-6 md:p-8 h-full" style={{ border: '1px solid rgba(71,202,241,0.15)' }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: 'rgba(71,202,241,0.1)', border: '1px solid rgba(71,202,241,0.25)', boxShadow: '0 0 30px rgba(71,202,241,0.15)' }}>
                  <i className={`${current.icon} text-[#47CAF1] text-2xl`}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[#47CAF1] text-xs font-bold">Step {step + 1} of {steps.length}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(71,202,241,0.08)', border: '1px solid rgba(71,202,241,0.2)', color: '#47CAF1' }}>~{current.time}</span>
                  </div>
                  <h3 className="text-white font-black text-xl">{current.title}</h3>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full rounded-full h-1 mb-6" style={{ background: 'rgba(71,202,241,0.1)' }}>
                <div
                  className="h-1 rounded-full transition-all duration-500"
                  style={{ width: `${((step + 1) / steps.length) * 100}%`, background: 'linear-gradient(90deg, #47CAF1, #6EDBFF)', boxShadow: '0 0 8px rgba(71,202,241,0.6)' }}
                ></div>
              </div>

              <p className="text-[#56626B] text-sm leading-relaxed mb-6">{current.desc}</p>

              <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(71,202,241,0.04)', border: '1px solid rgba(71,202,241,0.1)' }}>
                <h4 className="text-[#47CAF1] font-bold text-xs mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-yellow-400"></i> Tips for This Step
                </h4>
                <ul className="space-y-2">
                  {current.tips.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-[#56626B] text-xs">
                      <i className="ri-check-line text-[#47CAF1] text-sm flex-shrink-0 mt-0.5"></i>{t}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl p-4" style={{ background: 'rgba(71,202,241,0.03)', border: '1px solid rgba(71,202,241,0.08)' }}>
                <h4 className="text-[#47CAF1] font-bold text-xs mb-2 flex items-center gap-2">
                  <i className="ri-information-line"></i> Important Information
                </h4>
                <p className="text-[#56626B] text-xs leading-relaxed">{current.note}</p>
              </div>

              <div className="mt-6 pt-4 border-t border-[#47CAF1]/10">
                <p className="text-center text-[#56626B] text-xs mb-3 sm:hidden">{step + 1} / {steps.length}</p>
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={step === 0}
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer disabled:opacity-30 flex-1 sm:flex-none sm:min-w-[7rem]"
                    style={{ background: 'rgba(71,202,241,0.06)', color: '#56626B', border: '1px solid rgba(71,202,241,0.1)' }}
                  >
                    <i className="ri-arrow-left-s-line"></i> Previous
                  </button>
                  <span className="hidden sm:inline text-[#56626B] text-xs shrink-0">{step + 1} / {steps.length}</span>
                  <button
                    type="button"
                    disabled={step === steps.length - 1}
                    onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                    className="flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer disabled:opacity-30 flex-1 sm:flex-none sm:min-w-[7rem]"
                    style={{ background: 'rgba(71,202,241,0.12)', color: '#47CAF1', border: '1px solid rgba(71,202,241,0.25)' }}
                  >
                    Next <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: 'ri-time-line', q: 'How long does registration take?', a: "Under 3 minutes. Fill in your details, verify your mobile, and you're ready to play at Bybid9." },
            { icon: 'ri-money-dollar-circle-line', q: 'Is registration free?', a: 'Yes, completely free. No credit card required to create a Bybid9 account.' },
            { icon: 'ri-customer-service-2-line', q: 'Need help registering?', a: 'Our 24/7 Bybid9 support team is available via live chat, Telegram, WhatsApp, and Facebook.' },
          ].map((faq) => (
            <div key={faq.q} className="glass-card rounded-xl p-4" style={{ border: '1px solid rgba(71,202,241,0.1)' }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg mb-3" style={{ background: 'rgba(71,202,241,0.08)' }}>
                <i className={`${faq.icon} text-[#47CAF1] text-sm`}></i>
              </div>
              <h4 className="text-white font-bold text-xs mb-1">{faq.q}</h4>
              <p className="text-[#56626B] text-xs leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
